import React, { useState } from 'react';
import { rateOrderProducts } from '../db';


export default function RatingModal({ isOpen, onClose, order, onSuccess }) {
  // Initialize ratings state for each item in the order
  const [ratings, setRatings] = useState(() => {
    return (order?.items || []).map(item => ({
      product_id: item.product_id || item.product?.id,
      name: item.product_name || item.product?.name,
      image: item.image_url || item.product?.images?.[0],
      rating: 5,
      review: '',
    }));
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !order) return null;


  const handleRatingChange = (idx, newRating) => {
    const newRatings = [...ratings];
    newRatings[idx].rating = newRating;
    setRatings(newRatings);
  };

  const handleReviewChange = (idx, newReview) => {
    const newRatings = [...ratings];
    newRatings[idx].review = newReview;
    setRatings(newRatings);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = ratings
        .filter(r => r.product_id)
        .map(r => ({
          product_id: r.product_id,
          rating: r.rating,
          review: r.review
        }));

      if (payload.length === 0) {
        onClose();
        return;
      }

      await rateOrderProducts(order.dbId || order.id, payload);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to submit ratings', error);
      alert('Failed to submit your ratings. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-4 md:p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <div>
            <h3 className="text-xl font-black text-[#1A1A2E]">Rate Your Items</h3>
            <p className="text-sm text-gray-500 mt-1">Order {order.reference}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 md:p-6 overflow-y-auto space-y-6 flex-1">
          {ratings.map((item, idx) => (
            <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-100">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-white rounded-lg border border-gray-200 overflow-hidden shrink-0">
                  {(() => {
                    const itemImg = Array.isArray(item.images)
                      ? item.images.find(img => !img.match(/\.(mp4|webm|ogg)$/i))
                      : (!item.image?.match(/\.(mp4|webm|ogg)$/i) ? item.image : null);
                    if (itemImg) {
                      return <img src={itemImg} alt={item.name} className="w-full h-full object-cover" />;
                    }
                    return <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">?</div>;
                  })()}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#1A1A2E] text-sm line-clamp-2">{item.name}</p>
                  <div className="flex gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingChange(idx, star)}
                        className={`transition-colors ${star <= item.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <textarea
                value={item.review}
                onChange={(e) => handleReviewChange(idx, e.target.value)}
                placeholder="Write a review (optional)"
                className="w-full bg-white border border-gray-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#F47B20] focus:border-transparent outline-none resize-none transition-shadow"
                rows={3}
              />
            </div>
          ))}
        </div>

        <div className="p-4 md:p-6 border-t border-gray-100 bg-white flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Skip
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-6 py-2.5 rounded-xl font-bold text-white bg-[#F47B20] hover:bg-[#E06A1B] transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Ratings'}
          </button>
        </div>
      </div>
    </div>
  );
}
